import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Container, Grid, TextField, Button, CircularProgress,
  Alert, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, Autocomplete, Tooltip, IconButton,
  Accordion, AccordionSummary, AccordionDetails, Divider
} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// ─── Paleta del sistema ───────────────────────────────────────────────────────
const S = {
  inputBg: { '& .MuiInputBase-root': { backgroundColor: '#302f2fff' } },
  headerCell: { fontWeight: 'bold', backgroundColor: '#313131', color: '#fff', fontFamily: 'Roboto', fontSize: '0.78rem', whiteSpace: 'pre-line', textAlign: 'center' },
  rowHover: { '&:hover': { backgroundColor: '#2a2a2a' } },
};

/**
 * Calcula el consumo de hilaza por materia prima.
 * total_m2  = metros_lineales × ancho_cm / 100
 * total_kg  = total_m2 × pesoMetroCuadrado(g/m²) / 1000
 * kg_hilo_i = total_kg × porcentaje_i / 100
 */
const calcularConsumoHilaza = (ref, cantidad) => {
  if (!ref.pesoMetroCuadrado || !ref.anchoTela) return null;
  const totalM2 = cantidad * (ref.anchoTela / 100);
  const totalKg = (totalM2 * ref.pesoMetroCuadrado) / 1000;
  const round2 = v => Math.round(v * 100) / 100;

  // Si solo hay una MP, asumimos 100 %
  const soloUna = !ref.materiaPrima2 && !ref.materiaPrima3;

  const materias = [];
  if (ref.materiaPrima1) {
    const pct = soloUna ? 100 : (ref.porcentajeMP1 ?? null);
    if (pct !== null) materias.push({ nombre: ref.materiaPrima1, porcentaje: pct, kg: round2(totalKg * pct / 100) });
  }
  if (ref.materiaPrima2 && ref.porcentajeMP2 != null) {
    materias.push({ nombre: ref.materiaPrima2, porcentaje: ref.porcentajeMP2, kg: round2(totalKg * ref.porcentajeMP2 / 100) });
  }
  if (ref.materiaPrima3 && ref.porcentajeMP3 != null) {
    materias.push({ nombre: ref.materiaPrima3, porcentaje: ref.porcentajeMP3, kg: round2(totalKg * ref.porcentajeMP3 / 100) });
  }
  return { totalM2: round2(totalM2), totalKg: round2(totalKg), materias };
};

const SS_KEY = 'programacionIA_estado';

const ProgramacionIA = () => {
  const [referencias, setReferencias] = useState([]);
  // items: array de { id, referencia: object|null, cantidad: string }
  const [items, setItems] = useState([{ id: 1, referencia: null, cantidad: '' }]);
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingRefs, setLoadingRefs] = useState(true);
  // resultados: array de { referencia: string, cantidad: number, data: apiResponse|null, error: string|null }
  const [resultados, setResultados] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchReferencias = async () => {
      setLoadingRefs(true);
      try {
        const token = localStorage.getItem('token');
        const res = await fetch('/api/programacion-ia/referencias', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) throw new Error('Error al cargar referencias');
        const data = await res.json();
        setReferencias(data);

        // Restaurar estado guardado en sessionStorage
        try {
          const guardado = sessionStorage.getItem(SS_KEY);
          if (guardado) {
            const parsed = JSON.parse(guardado);
            if (parsed.items && Array.isArray(parsed.items)) {
              const restored = parsed.items.map(it => ({
                id: it.id || Date.now() + Math.random(),
                referencia: it.refCodigo ? (data.find(r => r.referencia === it.refCodigo) || null) : null,
                cantidad: it.cantidad || ''
              }));
              setItems(restored.length > 0 ? restored : [{ id: 1, referencia: null, cantidad: '' }]);
            }
            if (parsed.fechaInicio) setFechaInicio(parsed.fechaInicio);
            if (parsed.fechaFin) setFechaFin(parsed.fechaFin);
            if (parsed.resultados) setResultados(parsed.resultados);
          }
        } catch { /* sessionStorage no disponible */ }

      } catch {
        setError('No se pudieron cargar las referencias. Verifica tu sesión.');
      } finally {
        setLoadingRefs(false);
      }
    };
    fetchReferencias();
  }, []);

  const addItem = () => {
    setItems(prev => [...prev, { id: Date.now(), referencia: null, cantidad: '' }]);
  };

  const removeItem = (id) => {
    if (items.length === 1) return;
    setItems(prev => prev.filter(i => i.id !== id));
  };

  const updateItem = (id, field, value) => {
    setItems(prev => prev.map(i => i.id === id ? { ...i, [field]: value } : i));
  };

  const handleLimpiar = () => {
    setItems([{ id: 1, referencia: null, cantidad: '' }]);
    setFechaInicio('');
    setFechaFin('');
    setResultados([]);
    setError('');
    sessionStorage.removeItem(SS_KEY);
  };

  const handleGenerar = async () => {
    setError('');
    setResultados([]);

    const validItems = items.filter(it => it.referencia && it.cantidad);
    if (validItems.length === 0) {
      setError('Agrega al menos una referencia con su cantidad.');
      return;
    }
    for (const it of validItems) {
      const cant = Number(it.cantidad);
      if (isNaN(cant) || cant <= 0) {
        setError(`Cantidad inválida para la referencia "${it.referencia.referencia}".`);
        return;
      }
    }
    if (fechaInicio && fechaFin && fechaInicio >= fechaFin) {
      setError('La fecha de inicio debe ser anterior a la fecha de fin.');
      return;
    }
    if ((fechaInicio && !fechaFin) || (!fechaInicio && fechaFin)) {
      setError('Debes ingresar ambas fechas o dejarlas vacías.');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const results = await Promise.all(
        validItems.map(async (it) => {
          try {
            const res = await fetch('/api/programacion-ia', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify({
                referencia: it.referencia.referencia,
                cantidadAFabricar: Number(it.cantidad),
                ...(fechaInicio && fechaFin ? { fechaInicio, fechaFin } : {})
              })
            });
            const data = await res.json();
            if (!res.ok) return { referencia: it.referencia.referencia, cantidad: Number(it.cantidad), data: null, error: data.message };
            return { referencia: it.referencia.referencia, cantidad: Number(it.cantidad), data, error: null };
          } catch (e) {
            return { referencia: it.referencia.referencia, cantidad: Number(it.cantidad), data: null, error: e.message };
          }
        })
      );
      setResultados(results);
      try {
        sessionStorage.setItem(SS_KEY, JSON.stringify({
          items: items.map(it => ({ id: it.id, refCodigo: it.referencia?.referencia || null, cantidad: it.cantidad })),
          fechaInicio,
          fechaFin,
          resultados: results
        }));
      } catch { /* sessionStorage lleno */ }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      {/* ── Encabezado ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4, mt: 4 }}>
        <SmartToyIcon sx={{ fontSize: 40, color: '#90caf9' }} />
        <Box>
          <Typography variant="h4" gutterBottom sx={{ fontFamily: 'Roboto', fontWeight: '300' }}>
            Programación Inteligente de Producción
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Roboto' }}>
            Recomendación óptima de máquina, operarios y tiempo — basada en datos históricos reales
          </Typography>
        </Box>
      </Box>

      {/* ── Formulario ── */}
      <Paper sx={{ p: 3, mb: 3, backgroundColor: '#1a1a1a', border: '1px solid #333' }}>
        {/* Periodo global (opcional) */}
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontFamily: 'Roboto' }}>
          Período de producción (opcional — aplica a todas las referencias)
        </Typography>
        <Grid container spacing={2} mb={3} alignItems="flex-start">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Fecha inicio"
              type="date"
              fullWidth
              value={fechaInicio}
              onChange={e => {
                const val = e.target.value;
                setFechaInicio(val);
                if (fechaFin && val >= fechaFin) setFechaFin('');
              }}
              InputLabelProps={{ shrink: true }}
              sx={S.inputBg}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Fecha fin"
              type="date"
              fullWidth
              value={fechaFin}
              onChange={e => setFechaFin(e.target.value)}
              inputProps={{ min: fechaInicio || undefined }}
              InputLabelProps={{ shrink: true }}
              sx={S.inputBg}
              helperText={fechaFin && fechaInicio && fechaFin <= fechaInicio ? 'Debe ser posterior al inicio' : ' '}
              error={!!(fechaFin && fechaInicio && fechaFin <= fechaInicio)}
            />
          </Grid>
        </Grid>

        <Divider sx={{ mb: 3, borderColor: '#333' }} />

        {/* Lista de referencias */}
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontFamily: 'Roboto' }}>
          Referencias a programar
        </Typography>

        {items.map((item, index) => (
          <Grid container spacing={2} key={item.id} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={referencias}
                getOptionLabel={op =>
                  `${op.referencia}${op.estructura ? ' — ' + op.estructura : ''}${op.materiaPrima1 ? ' (' + op.materiaPrima1 + ')' : ''}`
                }
                value={item.referencia}
                onChange={(_, val) => updateItem(item.id, 'referencia', val)}
                loading={loadingRefs}
                noOptionsText="Sin referencias disponibles"
                renderInput={params => (
                  <TextField
                    {...params}
                    label={`Referencia ${index + 1}`}
                    placeholder="Buscar referencia..."
                    InputLabelProps={{ shrink: true }}
                    sx={S.inputBg}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>{loadingRefs ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</>
                      )
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={9} md={4}>
              <TextField
                label="Cantidad (metros)"
                type="number"
                fullWidth
                value={item.cantidad}
                onChange={e => updateItem(item.id, 'cantidad', e.target.value)}
                inputProps={{ min: 1, step: 1 }}
                placeholder="Ej: 500"
                sx={S.inputBg}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={3} md={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Tooltip title="Quitar referencia">
                <span>
                  <IconButton
                    onClick={() => removeItem(item.id)}
                    disabled={items.length === 1}
                    color="error"
                    size="large"
                  >
                    <DeleteIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Grid>
          </Grid>
        ))}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, flexWrap: 'wrap', gap: 1 }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<AddIcon />}
            onClick={addItem}
            disabled={loading}
            sx={{ fontFamily: 'Roboto' }}
          >
            Agregar referencia
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              color="inherit"
              sx={{ height: '44px', fontFamily: 'Roboto' }}
              onClick={handleLimpiar}
              disabled={loading}
            >
              Limpiar
            </Button>
            <Button
              variant="contained"
              color="primary"
              sx={{ height: '44px', fontFamily: 'Roboto', fontWeight: 'bold', px: 4 }}
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SmartToyIcon />}
              onClick={handleGenerar}
              disabled={loading}
            >
              {loading ? 'Calculando...' : 'Generar Programación'}
            </Button>
          </Box>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* ── Resultados ── */}
      {resultados.length > 0 && (
        <>
          {/* Tabla resumen combinada */}
          <Typography variant="h6" sx={{ mb: 1, fontFamily: 'Roboto', fontWeight: '300' }}>
            Tabla de Programación — {resultados.length} referencia(s) consultada(s)
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            Se muestra la opción óptima por referencia · 2 turnos/día de 8 h
            {resultados[0]?.data?.periodoProduccion && (
              <> · Disponibilidad verificada{' '}
                <strong>{resultados[0].data.periodoProduccion.fechaInicio}</strong> →{' '}
                <strong>{resultados[0].data.periodoProduccion.fechaFin}</strong>
              </>
            )}
            {!resultados[0]?.data?.periodoProduccion && <> · <em>Sin fechas — disponibilidad no verificada</em></>}
          </Typography>

          <TableContainer component={Paper} sx={{ mb: 4 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Referencia', 'Estructura', 'Cantidad\n(m)', 'Máquina\nÓptima', 'Disponibilidad', 'Eficiencia\n(%)', 'Prod. efectiva\n(m/turno)', 'Turnos', 'Días\nEstimados', 'Consumo Hilaza\n(kg)', 'Operarios Recomendados', 'Datos'].map(h => (
                    <TableCell key={h} sx={S.headerCell}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {resultados.map(r => {
                  if (r.error || !r.data) {
                    return (
                      <TableRow key={r.referencia} sx={S.rowHover}>
                        <TableCell><strong>{r.referencia}</strong></TableCell>
                        <TableCell colSpan={10}>
                          <Chip label={r.error || 'Sin datos'} size="small" color="error" />
                        </TableCell>
                      </TableRow>
                    );
                  }
                  const optima = r.data?.recomendaciones?.find(rec => rec.esOptima);
                  if (!optima) return null;
                  return (
                    <TableRow key={r.referencia} sx={{ ...S.rowHover, borderLeft: `3px solid ${optima.disponible !== false ? '#4caf50' : '#ff9800'}` }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <EmojiEventsIcon sx={{ color: '#f9a825', fontSize: 15 }} />
                          <Typography variant="body2" fontWeight="bold">{r.referencia}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.75rem' }}>{r.data.referencia.estructura || '—'}</TableCell>
                      <TableCell align="center"><strong>{r.cantidad}</strong></TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight="bold" color="primary">{optima.machineNumber}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        {!r.data.periodoProduccion
                          ? <Chip label="Sin verificar" size="small" variant="outlined" />
                          : optima.disponible
                          ? <Chip icon={<CheckCircleIcon />} label="Libre" size="small" color="success" />
                          : (
                            <Tooltip title={`Ref. asignada: ${optima.ocupacion?.referenciaAsignada || 'desconocida'}`}>
                              <Chip icon={<BlockIcon />} label="Ocupada" size="small" color="error" />
                            </Tooltip>
                          )
                        }
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={`${optima.eficienciaPromedio}%`}
                          size="small"
                          color={optima.eficienciaPromedio >= 85 ? 'success' : optima.eficienciaPromedio >= 70 ? 'warning' : 'error'}
                        />
                      </TableCell>
                      <TableCell align="center"><strong>{optima.produccionMetrosTurno}</strong></TableCell>
                      <TableCell align="center">{optima.turnosNecesarios}</TableCell>
                      <TableCell align="center">
                        <Chip label={`${optima.diasEstimados} días`} size="small" color={optima.disponible !== false ? 'success' : 'warning'} />
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const c = calcularConsumoHilaza(r.data.referencia, r.cantidad);
                          if (!c) return <Typography variant="caption" color="text.secondary">Sin datos de ancho/peso</Typography>;
                          return (
                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block">Total: <strong>{c.totalKg} kg</strong></Typography>
                              {c.materias.map(m => (
                                <Chip key={m.nombre} label={`${m.nombre}: ${m.kg} kg (${m.porcentaje}%)`} size="small" variant="outlined" sx={{ mr: 0.3, mb: 0.3, fontSize: '0.6rem' }} />
                              ))}
                            </Box>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        {optima.operariosRecomendados.length > 0
                          ? optima.operariosRecomendados.map(op => (
                              <Chip key={op.id} label={op.nombre} size="small" variant="outlined" color="primary" sx={{ mr: 0.5, mb: 0.3, fontSize: '0.65rem' }} />
                            ))
                          : <Typography variant="caption" color="text.secondary">—</Typography>
                        }
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={optima.conDatosReales ? 'Datos reales últimos 30 días' : 'Sin lecturas recientes — eficiencia estimada 85%'}>
                          <Chip label={optima.conDatosReales ? 'Real' : 'Estimado'} size="small" color={optima.conDatosReales ? 'primary' : 'warning'} variant="outlined" />
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Detalle expandible por referencia */}
          <Typography variant="subtitle1" sx={{ mb: 2, fontFamily: 'Roboto' }}>
            Detalle por referencia — todas las máquinas compatibles
          </Typography>

          {resultados.map(r => {
            if (r.error || !r.data) {
              return (
                <Alert key={r.referencia} severity="error" sx={{ mb: 2 }}>
                  {r.referencia}: {r.error}
                </Alert>
              );
            }
            return (
              <Accordion
                key={r.referencia}
                sx={{ mb: 2, backgroundColor: '#1d1d1d', border: '1px solid #333' }}
                defaultExpanded={resultados.length === 1}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography fontWeight="bold" color="primary">{r.referencia}</Typography>
                    <Chip label={`${r.cantidad} m`} size="small" variant="outlined" />
                    <Chip label={`${r.data.recomendaciones.length} máquinas`} size="small" />
                    {r.data.referencia.estructura && (
                      <Typography variant="caption" color="text.secondary">{r.data.referencia.estructura}</Typography>
                    )}
                    {(() => {
                      const c = calcularConsumoHilaza(r.data.referencia, r.cantidad);
                      if (!c) return null;
                      return (
                        <Tooltip title={c.materias.map(m => `${m.nombre}: ${m.kg} kg (${m.porcentaje}%)`).join(' | ')}>
                          <Chip label={`Hilaza total: ${c.totalKg} kg`} size="small" color="info" variant="outlined" sx={{ fontWeight: 'bold' }} />
                        </Tooltip>
                      );
                    })()}
                    {!r.data.recomendaciones?.find(rec => rec.esOptima)?.conDatosReales && (
                      <Tooltip title="Sin lecturas recientes. Se usa eficiencia estimada 85%.">
                        <Chip icon={<InfoOutlinedIcon />} label="Eficiencia estimada" size="small" variant="outlined" color="warning" />
                      </Tooltip>
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          {['#', 'Máquina', 'Disponibilidad', 'Rend. Práctico\n(m/turno)', 'Eficiencia\n(%)', 'Prod. efectiva\n(m/turno)', 'Turnos', 'Horas', 'Días', 'Operarios', 'Datos'].map(h => (
                            <TableCell key={h} sx={S.headerCell}>{h}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {r.data.recomendaciones.map(rec => (
                          <TableRow key={rec.machineNumber} sx={{ ...S.rowHover, opacity: (!r.data.periodoProduccion || rec.disponible) ? 1 : 0.7 }}>
                            <TableCell align="center">
                              {rec.esOptima
                                ? <EmojiEventsIcon sx={{ color: '#f9a825', fontSize: 18, verticalAlign: 'middle' }} />
                                : <Typography variant="body2" color="text.secondary">{rec.ranking}</Typography>
                              }
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="body2" fontWeight={rec.esOptima ? 'bold' : 'normal'} color={rec.esOptima ? 'primary' : 'inherit'}>
                                {rec.machineNumber}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              {!r.data.periodoProduccion
                                ? <Chip label="Sin verificar" size="small" variant="outlined" />
                                : rec.disponible
                                ? <Chip icon={<CheckCircleIcon />} label="Libre" size="small" color="success" />
                                : (
                                  <Tooltip title={`Ref. asignada: ${rec.ocupacion?.referenciaAsignada || 'desconocida'}`}>
                                    <Chip icon={<BlockIcon />} label="Ocupada" size="small" color="error" />
                                  </Tooltip>
                                )
                              }
                            </TableCell>
                            <TableCell align="center">{rec.rendimientoPractico}</TableCell>
                            <TableCell align="center">
                              <Chip
                                label={`${rec.eficienciaPromedio}%`}
                                size="small"
                                color={rec.eficienciaPromedio >= 85 ? 'success' : rec.eficienciaPromedio >= 70 ? 'warning' : 'error'}
                              />
                            </TableCell>
                            <TableCell align="center"><strong>{rec.produccionMetrosTurno}</strong></TableCell>
                            <TableCell align="center">{rec.turnosNecesarios}</TableCell>
                            <TableCell align="center">{rec.horasProduccion}h</TableCell>
                            <TableCell align="center">
                              <Chip label={`${rec.diasEstimados} días`} size="small" color={rec.esOptima ? (rec.disponible !== false ? 'success' : 'warning') : 'default'} />
                            </TableCell>
                            <TableCell>
                              {rec.operariosRecomendados.length > 0
                                ? rec.operariosRecomendados.map(op => (
                                    <Chip key={op.id} label={op.nombre} size="small" variant="outlined" color="primary" sx={{ mr: 0.5, mb: 0.3, fontSize: '0.65rem' }} />
                                  ))
                                : <Typography variant="caption" color="text.secondary">—</Typography>
                              }
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title={rec.conDatosReales ? 'Datos reales últimos 30 días' : 'Sin lecturas recientes — eficiencia estimada 85%'}>
                                <Chip label={rec.conDatosReales ? 'Real' : 'Estimado'} size="small" color={rec.conDatosReales ? 'primary' : 'warning'} variant="outlined" />
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            );
          })}

          <Box sx={{ mt: 2, p: 2, backgroundColor: '#1d1d1d', borderRadius: 1, border: '1px solid #333' }}>
            <Typography variant="caption" color="text.secondary">
              <strong>Metodología:</strong> Producción efectiva = (rev. promedio/día ÷ turnos) ÷ rev/metro.
              Operarios recomendados = mayor cantidad de turnos en esa máquina (últimos 30 días). Tiempos: 2 turnos/día de 8 horas.
            </Typography>
          </Box>
        </>
      )}
    </Container>
  );
};

export default ProgramacionIA;

